'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Check } from 'lucide-react';
import { useTheme } from 'next-themes';
import { AgentAudioVisualizerAura } from '@/components/agents-ui/agent-audio-visualizer-aura';
import { VoiceAgentModal, type MomentoVoice } from '@/components/livekit/VoiceAgentModal';
import { useSupabaseAuth } from '@/lib/auth/simplified/SupabaseAuthContext';
import { toast } from 'sonner';

const TESSA_VOICE: MomentoVoice = {
  id: 'Ashley',
  name: 'Tessa',
  description: 'Ready to share something great?',
  avatar: '/voices/tessa.png',
};

export default function LandingHero() {
  const [activeVoice, setActiveVoice] = useState<MomentoVoice | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const auth = useSupabaseAuth();
  const router = useRouter();
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const handleOrbClick = async () => {
    if (!auth.isAuthenticated) {
      setActiveVoice(TESSA_VOICE);
      return;
    }
    setIsStarting(true);
    try {
      const res = await fetch('/api/agent/interview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voiceId: 'Ashley', voiceName: 'Tessa' }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to start');
      const data = await res.json();
      const params = new URLSearchParams({ token: data.token, serverUrl: data.serverUrl });
      router.push(`/rooms/${data.roomName}?${params.toString()}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start conversation');
      setIsStarting(false);
    }
  };

  const isDark = mounted && resolvedTheme === 'dark';

  return (
    <section className="relative py-20 md:py-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-200px] right-[-200px] w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(239,68,68,0.05)_0%,transparent_70%)]" />
      </div>

      <div className="max-w-[1200px] mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#ECFDF5] text-[#059669] px-3.5 py-1.5 rounded-full text-[13px] font-semibold mb-6">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 7l5 5-5 5M6 7l5 5-5 5" />
              </svg>
              Employee content gets 8x more engagement
            </div>

            <h1 className="text-[42px] md:text-[50px] font-bold leading-[1.08] tracking-[-2px] mb-5 text-foreground">
              Your meetings are worth more than{' '}
              <span className="bg-gradient-to-r from-[#EF4444] to-[#F97316] bg-clip-text text-transparent">
                you think.
              </span>
            </h1>

            <p className="text-[17px] leading-[1.8] text-muted-foreground mb-8 max-w-[480px] tracking-[0.01em]">
              One link for booking, video calls, and AI-powered highlights from every conversation.
              <br className="hidden sm:block" /><br className="hidden sm:block" />
              Talk with an AI guide or a real person — every session becomes authentic, shareable content.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <Link
                href="/start"
                className="inline-flex items-center justify-center gap-2 bg-[#EF4444] text-white px-7 py-3.5 rounded-full text-[15px] font-semibold shadow-[0_4px_14px_rgba(239,68,68,0.3)] hover:bg-[#DC2626] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(239,68,68,0.4)] transition-all"
              >
                Get Your Free Booking Page
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
              <button
                onClick={() => document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center px-7 py-3.5 bg-background text-foreground border border-border rounded-full text-[15px] font-semibold hover:bg-muted transition-all"
              >
                See how it works
              </button>
            </div>

            <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
              <Check className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0" />
              Free forever · No credit card · Your page at apprendo.co/@you
            </div>
          </div>

          <div className="relative flex items-center justify-center min-h-[440px]">
            <div
              className="absolute top-10 left-0 lg:-left-8 bg-background border border-border rounded-[10px] p-3 px-4 shadow-lg flex items-center gap-2.5 z-10"
              style={{ animation: 'floatY 5s ease-in-out infinite 1s' }}
            >
              <div className="w-9 h-9 rounded-[10px] bg-[#FEF2F2] text-[#EF4444] flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              </div>
              <div>
                <div className="text-[13px] font-semibold text-foreground">26 Moments Found</div>
                <div className="text-[11px] text-muted-foreground">from a 20-min conversation</div>
              </div>
            </div>

            <button
              onClick={handleOrbClick}
              disabled={isStarting}
              className="relative cursor-pointer hover:scale-[1.04] transition-transform duration-300 flex flex-col items-center focus:outline-none"
            >
              <div className={`w-[280px] h-[280px] md:w-[320px] md:h-[320px] rounded-full overflow-hidden flex items-center justify-center ${isDark ? 'bg-gray-950' : 'bg-gray-100'}`}>
                <AgentAudioVisualizerAura
                  size="xl"
                  state="idle"
                  color="#1FD5F9"
                  colorShift={0.5}
                  themeMode={isDark ? 'dark' : 'light'}
                  className="w-full h-full"
                />
              </div>
              <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 bg-foreground/92 text-background px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap flex items-center gap-2 z-10">
                <span
                  className="w-2 h-2 bg-[#10B981] rounded-full"
                  style={{ animation: 'pulseDot 2s infinite' }}
                />
                {isStarting ? 'Connecting...' : 'Talk to Tessa — AI Guide'}
              </div>
            </button>

            <div
              className="absolute bottom-12 right-0 lg:-right-12 bg-background border border-border rounded-[10px] p-4 px-5 shadow-lg max-w-[230px] z-10"
              style={{ animation: 'floatY 4s ease-in-out infinite' }}
            >
              <div className="text-[11px] bg-muted px-2 py-1 rounded text-[#EF4444] mb-2.5 font-mono">
                apprendo.co/@yourname
              </div>
              <ul className="space-y-1">
                {['HD video + auto-recording', 'AI-extracted moments', 'Calendar sync', 'Stripe payments built in'].map((feature) => (
                  <li key={feature} className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                    <Check className="w-3 h-3 text-[#10B981] flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {activeVoice && (
        <VoiceAgentModal
          open={!!activeVoice}
          onOpenChange={(isOpen) => { if (!isOpen) setActiveVoice(null); }}
          voice={activeVoice}
        />
      )}
    </section>
  );
}
